import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  constructor() { }

  /**
  * Sauvegarde un objet dans le localStorage
  * @param key Clé sous laquelle les données seront stockées
  * @param value Données à sauvegarder
  */
  setItem(key: string, value: unknown): void {
    try {

      if (value === undefined) {
        console.warn(`Tentative de sauvegarde d'une valeur undefined pour la clé "${key}"`);
        return;
      }
      let serializedValue = JSON.stringify(value);

      localStorage.setItem(key, serializedValue);  // Sauvegarde dans le localStorage
    } catch (error: any) {
      if (error.name === 'QuotaExceededError') {
        console.warn(`Espace de stockage saturé. Impossible d'enregistrer la clé "${key}"`);
        // Option : vider des données anciennes
        // localStorage.removeItem('ancienneCle');
      } else {
        console.error('Erreur lors de la sauvegarde dans le localStorage', error);
      }
    }
  }

  /**
   * Récupère un objet depuis le localStorage
   * @param key Clé sous laquelle les données sont stockées
   * @returns Données récupérées ou null si aucune donnée n'est trouvée
   */
  getItem<T>(key: string): T | null {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) as T : null;
    } catch (error) {
      console.error('Erreur lors de la lecture du localStorage', error);
      return null;
    }
  }

  /**
    * Récupère un objet de collection depuis le localStorage pour le mettre à jour
    * @param key Clé sous laquelle les données sont stockées
    * @returns Données récupérées ou null si aucune donnée n'est trouvée
    */
  appendCollectionItems<T>(key: string, items: any[]): void {
    try {
      let localItems: any = this.getItem(key); // Récupère et parse les données

      if (!localItems) {
        localItems = [];
      }

      items.forEach(i => {
        let localItemIndex = localItems.findIndex((li: any) => li.id == i.id)

        if (localItemIndex >= 0) {
          localItems[localItemIndex] = i;
        } else {
          localItems.push(i);
        }
      })

      this.setItem(key, localItems);
      return localItems;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du localStorage', error);
    }
  }

  /**
   * Récupère un objet depuis le localStorage pour le mettre à jour
   * @param key Clé sous laquelle les données sont stockées
   * @returns Données récupérées ou null si aucune donnée n'est trouvée
   */
  appendItem<T>(key: string, value: any): void {
    try {
      let item: any = this.getItem(key); // Récupère et parse les données
      if (Array.isArray(item)) {
        if (Array.isArray(value)) {
          item = item.concat(value);
        } else {
          item.push(value);
        }
      } else if (typeof value == 'object' && typeof item == 'object') {
        Object.keys(value).forEach(k => {
          item[k] = value[k];
        });
      } else {
        item = value;
      }
      this.setItem(key, item);
      return item;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du localStorage', error);
    }
  }

  /**
   * Supprime un élément du localStorage
   * @param key Clé sous laquelle les données sont stockées
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);  // Supprime l'élément du localStorage
    } catch (error) {
      console.error('Erreur lors de la suppression du localStorage', error);
    }
  }

  /**
   * Vérifie si un élément existe dans le localStorage
   * @param key Clé sous laquelle les données sont stockées
   * @returns True si l'élément existe, false sinon
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
 * Supprime des éléments spécifiques d'une collection dans le localStorage
 * @param key Clé sous laquelle la collection est stockée
 * @param itemsToRemove Liste des éléments à supprimer (doivent contenir un `id`)
 */
  deleteCollectionItem<T>(key: string, itemsToRemove: any[]): void {
    try {
      let collection: any[] = this.getItem(key) as any[]; // Récupère la collection actuelle
      if (!Array.isArray(collection)) {
        console.warn(`Aucune collection trouvée pour la clé : ${key}`);
        return;
      }

      // Filtre les éléments à supprimer
      collection = collection.filter(item => {
        const isToRemove = itemsToRemove.some(toRemove => {
          let removeId = toRemove._id || toRemove.id || toRemove;
          let itemId = item.id || item._id || item;

          const match = removeId === itemId;
          return match;
        });
        return !isToRemove;
      });

      // Met à jour le local storage
      this.setItem(key, collection);
    } catch (error) {
      console.error('Erreur lors de la suppression d\'éléments dans le localStorage', error);
    }
  }

  /**
   * Vide le localStorage entier
   */
  clear(): void {
    try {
      localStorage.clear();  // Vide tout le localStorage
    } catch (error) {
      console.error('Erreur lors de la vidange du localStorage', error);
    }
  }

  /** 
  * Vide les variables spécifiques d'un projet dans le localStorage
  */
  clearProject(database: string): void {
    try {

      const keysToRemove: string[] = [];

      // Vérifier que la clé commence bien par "database_"
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(database + "_")) {
          keysToRemove.push(key);
        }
      }

      // Supprimer uniquement ces clés
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

    } catch (error) {
      console.error('Erreur lors de la suppression des éléments du localStorage', error);
    }
  }

  mergeCollectionItems<T>(key: string, items: Partial<T>[]): T[] {
    const localItems: T[] = this.getItem<T[]>(key) || [];

    items.forEach(update => {
      const id = (update as any).id || (update as any)._id;
      const index = localItems.findIndex((i: any) => (i as any).id === id || (i as any)._id === id);

      if (index >= 0) {
        localItems[index] = {
          ...(localItems[index] as any),
          ...(update as any)
        } as T;
      } else {
        localItems.push(update as T);
      }
    });

    this.setItem(key, localItems);
    return localItems;
  }
}
